<script setup lang="ts">
// Watch an input live with its sound, on its Live tab (monitor.ts). Nothing
// runs until Watch is pressed: the node encodes a source for monitoring
// only while somebody watches it. On central, a link opens it large on the
// watch page.
import { computed, ref, watch } from 'vue'
import { nodeId } from '../../api'
import { pairsOf, stateTone, stopped, useMonitor } from '../../monitor'

// pairLabels: an encode's tracks by name; an input's are its channels
const props = defineProps<{ source: string; channels?: number; pairLabels?: string[] }>()
const video = ref<HTMLVideoElement | null>(null)
const m = useMonitor(
    computed(() => nodeId),
    computed(() => props.source),
    video,
)
const { state, pair, rtt } = m
watch(
    () => props.source,
    () => m.stop(),
)
const labels = computed(() => props.pairLabels ?? pairsOf(props.channels).map((k) => `ch ${2 * k + 1}+${2 * k + 2}`))
const large = computed(() => `/watch?node=${nodeId}&source=${encodeURIComponent(props.source)}`)
</script>

<template>
    <div class="mt-3 rounded-md border border-line px-2.5 py-2 text-xs" data-test="monitor-player">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <b class="text-[13px] font-semibold" title="With sound, as near live as the line allows">Watch live</b>
            <span class="ml-auto inline-flex items-center gap-2">
                <a v-if="nodeId" :href="large" class="text-xs" data-test="monitor-large" @click="m.stop()"
                    >Open large</a
                >
                <button v-if="stopped(state)" class="text-xs" data-test="monitor-watch" @click="m.start">Watch</button>
                <button v-else class="text-xs" @click="m.stop">Stop</button>
            </span>
        </div>
        <template v-if="state !== 'idle'">
            <video
                ref="video"
                class="mt-2 block aspect-video w-full rounded bg-black object-contain"
                autoplay
                playsinline
                data-test="monitor-video"
            />
            <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <label class="m-0 inline-flex items-center gap-1 text-fg-2"
                    >listen
                    <select v-model.number="pair" class="w-auto text-xs" data-test="monitor-pair">
                        <option :value="-1">nothing</option>
                        <option v-for="(l, k) in labels" :key="k" :value="k">{{ l }}</option>
                    </select></label
                >
                <span class="h-4 tabular-nums" :class="stateTone(state) || 'text-fg-2'" role="status">{{
                    m.label()
                }}</span>
                <span v-if="m.routeLabel()" class="text-fg-2" data-test="monitor-route">{{ m.routeLabel() }}</span>
                <span v-if="rtt !== null" class="inline-block min-w-20 text-fg-2 tabular-nums"
                    >{{ rtt.toFixed(0) }} ms round trip</span
                >
            </div>
        </template>
    </div>
</template>
